import exifr from 'exifr';
import { metadataService, PhotoDetails } from './metadataService';
import { getHeicSupportCached } from './heicSupportService';
import { convertHeicBlobToJpeg } from '../utils/fileUtils';

/**
 * In local development, rewrites Azurite blob URLs to relative paths so that
 * fetch() calls go through the CRA dev server proxy (setupProxy.js) instead of
 * directly to localhost:10000, avoiding CORS issues.
 *
 * e.g. http://localhost:10000/devstoreaccount1/data/... → /devstoreaccount1/data/...
 *
 * In non-local environments, returns the URL unchanged.
 */
export function getDevProxiedUrl(url: string): string {
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
     window.location.hostname.startsWith('127.0.0.1') ||
     window.location.hostname.endsWith('.app.github.dev'))
  ) {
    return url.replace(/^https?:\/\/localhost:1000[0-2]/, '');
  }
  return url;
}

/**
 * Maximum number of full-resolution image blobs kept in memory at once. When
 * exceeded, least-recently-used entries are evicted (URLs revoked, blob refs
 * dropped) so we don't accumulate unbounded memory while users browse large
 * galleries. Tuned for ~200 typical-photo blobs (a few hundred MB worst case).
 */
const MAX_CACHED_BLOBS = 200;

class ImageLoaderService {
  // Map preserves insertion order; we treat it as an LRU by deleting + re-
  // inserting on access so the head is always the least-recently-used entry.
  private blobUrls: Map<string, string> = new Map();
  private blobs: Map<string, Blob> = new Map();
  private heicConversionFailures: Set<string> = new Set();

  /**
   * Marks a key as most-recently-used by re-inserting it at the end of the
   * Maps' iteration order. Safe to call when the key is absent.
   */
  private touch(key: string): void {
    const url = this.blobUrls.get(key);
    if (url !== undefined) {
      this.blobUrls.delete(key);
      this.blobUrls.set(key, url);
    }
    const blob = this.blobs.get(key);
    if (blob !== undefined) {
      this.blobs.delete(key);
      this.blobs.set(key, blob);
    }
  }

  /**
   * Evicts least-recently-used entries until under the cache cap.
   * Revokes object URLs to release memory.
   */
  private evictIfNeeded(): void {
    while (this.blobUrls.size > MAX_CACHED_BLOBS) {
      // First key in iteration order = oldest entry
      const oldestKey = this.blobUrls.keys().next().value;
      if (oldestKey === undefined) break;
      const oldestUrl = this.blobUrls.get(oldestKey);
      if (oldestUrl) {
        try { URL.revokeObjectURL(oldestUrl); } catch { /* ignore */ }
      }
      this.blobUrls.delete(oldestKey);
      this.blobs.delete(oldestKey);
      this.heicConversionFailures.delete(oldestKey);
    }
  }

  /**
   * Loads an image from a URL and creates a blob URL for better performance
   * Also parses and stores EXIF metadata if available
   * Converts HEIC images to JPEG for browser compatibility when native HEIC is not supported
   *
   * @param heicSupported - Whether the browser natively supports HEIC rendering.
   *   When true, HEIC blobs are used directly (preserving HDR/P3/10-bit).
   *   When false, HEIC is converted to JPEG via heic-decode + canvas.
   *   When undefined/null, falls back to checking the cached detection result.
   *
   * @returns blobUrl and blob on success. When HEIC conversion fails,
   *   returns { blobUrl: null, blob: null } so the caller can keep
   *   any medium-quality preview it already has instead of showing a placeholder.
   */
  async loadImage(
    photoUrl: string,
    albumId: string,
    photoIndex: number,
    heicSupported?: boolean | null
  ): Promise<{ blobUrl: string | null; blob: Blob | null }> {
    const key = `${albumId}-${photoIndex}`;

    // Check if we already have this image's blob URL cached
    // Return the existing blob URL - don't revoke and recreate it
    // as it might still be displayed in the UI
    const existingBlobUrl = this.blobUrls.get(key);
    if (existingBlobUrl && this.blobs.has(key)) {
      console.log(`Using cached blob URL for ${key}`);
      this.touch(key);
      return {
        blobUrl: existingBlobUrl,
        blob: this.blobs.get(key)!
      };
    }
    
    // If we only have the blob but not the URL (shouldn't happen normally),
    // create a new URL from the cached blob
    if (this.blobs.has(key)) {
      console.log(`Recreating blob URL for ${key}`);
      const blob = this.blobs.get(key)!;
      const blobUrl = URL.createObjectURL(blob);
      this.blobUrls.set(key, blobUrl);
      this.touch(key);
      this.evictIfNeeded();
      return { blobUrl, blob };
    }

    try {
      console.log(`Fetching image for ${key}`);
      const response = await fetch(getDevProxiedUrl(photoUrl));
      if (!response.ok) {
        if (response.status === 403 || response.status === 401) {
          throw new Error('Photo access token expired or unauthorized');
        }
        throw new Error(`Failed to fetch image (HTTP ${response.status})`);
      }

      let blob = await response.blob();

      // Check if this is a HEIC file and handle accordingly
      const isHeic = this.isHeicFile(blob, photoUrl);
      // Keep reference to original blob for metadata extraction (HEIC→JPEG conversion strips EXIF)
      const originalBlob = blob;
      if (isHeic) {
        // Resolve HEIC support: use explicit parameter, fall back to cached detection result
        const nativeHeic = heicSupported ?? getHeicSupportCached() ?? false;

        if (nativeHeic) {
          // Browser supports HEIC natively — use the blob directly.
          // This preserves HDR, wide gamut (Display P3), and 10-bit depth.
          console.log('Using native HEIC rendering (HDR/P3 preserved)');
        } else {
          // Browser does NOT support HEIC — decode with heic-decode and render to canvas as JPEG
          try {
            console.log('Converting HEIC image to JPEG for display (browser lacks native HEIC support)');
            const convertedBlob = await convertHeicBlobToJpeg(blob);
            blob = convertedBlob;
            console.log('HEIC conversion successful');
          } catch (conversionError) {
            console.warn('HEIC conversion failed:', conversionError);
            // Track conversion failure for this photo
            this.heicConversionFailures.add(key);

            // Parse metadata from the original HEIC blob before returning null
            await this.parseAndStoreMetadata(originalBlob, albumId, photoIndex, true);

            // Return null so the caller keeps showing the medium-quality preview
            // instead of replacing it with an unusable placeholder
            return { blobUrl: null, blob: null };
          }
        }
      }

      const blobUrl = URL.createObjectURL(blob);

      // Store the blob URL and blob for caching
      this.blobUrls.set(key, blobUrl);
      this.blobs.set(key, blob);
      this.evictIfNeeded();

      // Parse and store metadata from the original blob (preserves EXIF for converted HEIC files)
      await this.parseAndStoreMetadata(originalBlob, albumId, photoIndex, isHeic);

      return { blobUrl, blob };
    } catch (err) {
      console.warn('Failed to preload image blob, falling back to remote URL:', err);
      return { blobUrl: null, blob: null };
    }
  }

  /**
   * Gets the blob URL for a specific photo
   */
  getBlobUrl(albumId: string, photoIndex: number): string | null {
    const key = `${albumId}-${photoIndex}`;
    const url = this.blobUrls.get(key);
    if (url) {
      this.touch(key);
      return url;
    }
    return null;
  }

  /**
   * Cleans up blob URLs for a specific album or all albums
   */
  cleanup(albumId?: string): void {
    if (albumId) {
      // Cleanup specific album
      const keysToDelete = Array.from(this.blobUrls.keys()).filter(key =>
        key.startsWith(`${albumId}-`)
      );
      keysToDelete.forEach(key => {
        const blobUrl = this.blobUrls.get(key);
        if (blobUrl) {
          URL.revokeObjectURL(blobUrl);
        }
        this.blobUrls.delete(key);
        this.blobs.delete(key);
        this.heicConversionFailures.delete(key);
      });
    } else {
      // Cleanup all blob URLs
      this.blobUrls.forEach(blobUrl => {
        URL.revokeObjectURL(blobUrl);
      });
      this.blobUrls.clear();
      this.blobs.clear();
      this.heicConversionFailures.clear();
    }
  }

  /**
   * Checks if a specific photo had a HEIC conversion failure (showing preview quality).
   */
  isHeicPlaceholder(albumId: string, photoIndex: number): boolean {
    const key = `${albumId}-${photoIndex}`;
    return this.heicConversionFailures.has(key);
  }

  /**
   * Checks if a blob or URL represents a HEIC file
   */
  isHeicFile(blob: Blob, photoUrl: string): boolean {
    // Check MIME type first
    if (blob.type === 'image/heic' || blob.type === 'image/heif') {
      return true;
    }

    // Check file extension from URL
    const extension = this.getFileExtension(photoUrl).toLowerCase();
    return extension === 'heic' || extension === 'heif';
  }

  /**
   * Parses EXIF metadata from a blob and stores it in the metadata service.
   */
  private async parseAndStoreMetadata(
    blob: Blob,
    albumId: string,
    photoIndex: number,
    isHeic: boolean
  ): Promise<void> {
    if (metadataService.getMetadata(albumId, photoIndex)) {
      return; // Already stored
    }

    try {
      const metadata = await exifr.parse(blob);
      const details: PhotoDetails = {};

      if (metadata && Object.keys(metadata).length > 0) {
        // Date
        if (metadata.DateTimeOriginal) {
          details.takenDate = new Date(metadata.DateTimeOriginal).toISOString();
        } else if (metadata.DateTime) {
          details.takenDate = new Date(metadata.DateTime).toISOString();
        }

        // Dimensions
        if (metadata.ImageWidth) details.width = metadata.ImageWidth;
        if (metadata.ImageHeight) details.height = metadata.ImageHeight;
        if (metadata.ExifImageWidth && !details.width) details.width = metadata.ExifImageWidth;
        if (metadata.ExifImageHeight && !details.height) details.height = metadata.ExifImageHeight;

        // GPS
        if (typeof metadata.latitude === 'number') details.latitude = metadata.latitude;
        if (typeof metadata.longitude === 'number') details.longitude = metadata.longitude;

        // Camera
        if (metadata.Make) details.cameraMake = String(metadata.Make).trim();
        if (metadata.Model) details.cameraModel = String(metadata.Model).trim();

        // Settings
        if (typeof metadata.FNumber === 'number') details.fNumber = metadata.FNumber;
        if (typeof metadata.FocalLength === 'number') details.focalLength = metadata.FocalLength;
        if (typeof metadata.ISO === 'number') details.iso = metadata.ISO;
        else if (typeof metadata.ISOSpeedRatings === 'number') details.iso = metadata.ISOSpeedRatings;
        if (typeof metadata.ExposureTime === 'number') details.exposureTime = metadata.ExposureTime;
      }

      metadataService.setMetadata(albumId, photoIndex, details);
    } catch (err) {
      console.warn('Failed to parse image metadata:', err);
      metadataService.setMetadata(albumId, photoIndex, {});
    }
  }

  /**
   * Extracts file extension from URL
   */
  getFileExtension(photoUrl: string): string {
    try {
      const url = new URL(photoUrl);
      const pathname = url.pathname;
      if (pathname.includes('.')) {
        return pathname.split('.').pop()?.split('?')[0] || 'jpg';
      }
    } catch {
      // If URL parsing fails, use default extension
    }
    return 'jpg';
  }
}

export const imageLoaderService = new ImageLoaderService();